import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { listTenantFeatures, updateTenantFeature } from "../lib/api";
import type { TenantFeature } from "../types";

/* 額度欄語義（Captain 2026-09-17）：留空＝跟預設；填了＝以此為準（admin 彈性覆寫，優先於方案）。
 * 有方案預設的功能要寫明具體數字，避免「無限制」誤導。 */
const QUOTA_PLACEHOLDERS: Record<string, string> = {
  media_library: "留空＝方案預設（Lite 3／Pro 15）",
};

export default function TenantFeaturesPage({ embedded = false }: { embedded?: boolean }) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [features, setFeatures] = useState<TenantFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    loadFeatures();
  }, [tenantId]);

  async function loadFeatures() {
    if (!tenantId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listTenantFeatures(tenantId);
      if (data.success) {
        setFeatures(data.features || []);
      } else {
        setError(data.error || "載入失敗");
      }
    } catch {
      setError("網路錯誤");
    } finally {
      setLoading(false);
    }
  }

  async function toggleFeature(feature: TenantFeature) {
    if (!tenantId) return;
    setSavingId(feature.id);
    try {
      const data = await updateTenantFeature(tenantId, feature.id, {
        enabled: !feature.enabled,
      });
      if (data.success) {
        setFeatures((prev) =>
          prev.map((f) =>
            f.id === feature.id ? { ...f, enabled: !f.enabled, source: "record" } : f
          )
        );
      } else {
        setError(data.error || "更新失敗");
      }
    } catch {
      setError("網路錯誤");
    } finally {
      setSavingId(null);
    }
  }

  async function updateQuota(feature: TenantFeature, quotaStr: string) {
    if (!tenantId) return;
    const quota = quotaStr.trim() === "" ? null : Number(quotaStr);
    if (quotaStr.trim() !== "" && (isNaN(quota as number) || (quota as number) < 0)) {
      setError("額度必須是 >= 0 的數字");
      return;
    }
    setSavingId(feature.id);
    try {
      const data = await updateTenantFeature(tenantId, feature.id, { quota });
      if (data.success) {
        setFeatures((prev) =>
          prev.map((f) => (f.id === feature.id ? { ...f, quota, source: "record" } : f))
        );
      } else {
        setError(data.error || "更新失敗");
      }
    } catch {
      setError("網路錯誤");
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div>
      {!embedded && (
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate("/tenants")}
            className="px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
          >
            ← 返回
          </button>
          <h1 className="text-2xl font-bold text-slate-100">🧩 功能模塊：{tenantId}</h1>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-rose-500/10 border border-rose-500/30">
          <p className="text-sm font-medium text-rose-400">❌ {error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400">載入中...</div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-400">
            「預設」表示按租戶現有配置自動推斷；「已設定」表示管理員已手動開關。
          </p>
          {features.map((f) => (
            <div
              key={f.id}
              className="bg-slate-900 rounded-xl border border-slate-800 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-slate-100">{f.name}</h3>
                  {f.paid && (
                    <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">
                      付費
                    </span>
                  )}
                  <span
                    className={`px-2 py-0.5 text-xs rounded ${
                      f.source === "record"
                        ? "bg-sky-500/20 text-sky-400"
                        : "bg-slate-700 text-slate-400"
                    }`}
                  >
                    {f.source === "record" ? "已設定" : "預設"}
                  </span>
                </div>
                <p className="text-sm text-slate-400">{f.description}</p>
              </div>

              <div className="flex items-center gap-4">
                {f.paid && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500">額度</label>
                    <input
                      type="number"
                      min={0}
                      defaultValue={f.quota ?? ""}
                      placeholder={QUOTA_PLACEHOLDERS[f.id] ?? "留空＝無限制"}
                      disabled={savingId === f.id}
                      onBlur={(e) => updateQuota(f, e.target.value)}
                      className="w-36 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-sm text-slate-100 focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                    />
                  </div>
                )}
                <button
                  onClick={() => toggleFeature(f)}
                  disabled={savingId === f.id}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                    f.enabled
                      ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                      : "bg-slate-700 hover:bg-slate-600 text-slate-300"
                  }`}
                >
                  {savingId === f.id ? "儲存中..." : f.enabled ? "已開啟" : "已關閉"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
